import { defineStore } from 'pinia'
import { computed, effectScope, markRaw, ref, type EffectScope } from 'vue'
import type { UploadSpeedStats } from '~/composables/useUploadSpeed'
import type { WithId } from "mongodb"

// One at a time: every upload gets the full bandwidth, and finished videos start transcoding sooner than when
// several crawl along in parallel.
const MAX_CONCURRENT_UPLOADS = 1

export type UploadKind = keyof UploadLimits

type UploadTarget = {
    url: string
    /** The multipart field the endpoint reads the file from. */
    field: string
    /** What the file picker offers. */
    accept?: string
    /** Rejects a file before it is sent. Without one, only the server decides. */
    isSupported?: (file: File) => boolean
    gallery: string
}

export const isSupportedVideoFile = (file: File) => (
    file.type.startsWith('video/') || file.name.toLowerCase().endsWith('.mov')
)

export const UPLOAD_TARGETS: Record<UploadKind, UploadTarget> = {
    image: {
        url: '/api/files/save-image',
        field: 'image',
        accept: 'image/*',
        isSupported: file => file.type.startsWith('image/'),
        gallery: '/image-gallery',
    },
    video: {
        url: '/api/files/video/save',
        field: 'video',
        accept: 'video/*,.mov',
        isSupported: isSupportedVideoFile,
        gallery: '/video-gallery',
    },
    audio: {
        url: '/api/files/audio/save',
        field: 'audio',
        accept: 'audio/*',
        isSupported: file => file.type.startsWith('audio/'),
        gallery: '/audio-gallery',
    },
    // The allowed file types are server configuration, so the server judges them.
    file: {
        url: '/api/files/file/save',
        field: 'file',
        gallery: '/files-gallery',
    },
}

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled'

export type UploadError =
    | { code: 'tooLarge', message: string }
    | { code: 'unsupported' | 'network' }
    | { code: 'http', status?: number, message?: string }

export type UploadItem = {
    id: string
    kind: UploadKind
    file: File
    name: string
    size: number
    status: UploadStatus
    progress: number
    speed?: UploadSpeedStats
    error?: UploadError
    contentId?: string
}

type UploadResponse = ApiResponse<{
    mongoDbDocument: WithId<BucketData>
}>

/**
 * Batch uploads for the asset galleries. Lives in a store rather than a page so uploads keep going while the user
 * navigates around the CMS. Only for uploads that are complete once the server has them: a form field must keep its
 * own upload, since its result has to land in that form.
 */
export const useUploadQueueStore = defineStore('uploadQueue', () => {
    const items = ref<UploadItem[]>([])
    /** Bumps on every successful upload of that kind, so a gallery can refresh. */
    const completed = ref<Record<UploadKind, number>>({ image: 0, video: 0, audio: 0, file: 0 })
    /** Files left out of a folder upload since the overview was last cleared. */
    const skipped = ref(0)
    // Resolved while the store is set up (inside a component), as they need the Nuxt app.
    const uploadLimits = {
        image: useUploadLimit('image'),
        video: useUploadLimit('video'),
        audio: useUploadLimit('audio'),
        file: useUploadLimit('file'),
    }

    // Per-item resources that must not become reactive or survive the item.
    const running = new Map<string, { scope: EffectScope, controller: AbortController }>()

    const pending = computed(() => items.value.filter(item => item.status === 'queued' || item.status === 'uploading'))
    const isActive = computed(() => pending.value.length > 0)
    const totals = computed(() => {
        const counted = items.value.filter(item => item.status !== 'cancelled')
        const bytes = counted.reduce((sum, item) => sum + item.size, 0)
        const sent = counted.reduce((sum, item) => sum + (item.status === 'done' ? item.size : item.size * item.progress / 100), 0)
        return {
            done: counted.filter(item => item.status === 'done').length,
            failed: counted.filter(item => item.status === 'error').length,
            count: counted.length,
            progress: bytes ? sent / bytes * 100 : 0,
        }
    })

    const find = (id: string) => items.value.find(item => item.id === id)

    const pump = () => {
        if (!import.meta.client) return
        const slots = MAX_CONCURRENT_UPLOADS - items.value.filter(item => item.status === 'uploading').length
        items.value.filter(item => item.status === 'queued').slice(0, Math.max(0, slots)).forEach(item => {
            void upload(item.id)
        })
    }

    const upload = async (id: string) => {
        const item = find(id)
        if (!item) return
        const target = UPLOAD_TARGETS[item.kind]
        // useUploadSpeed ties its timer to the current effect scope; give each upload its own.
        const scope = effectScope(true)
        const speed = scope.run(() => useUploadSpeed())!
        const controller = new AbortController()
        running.set(id, { scope, controller })
        item.status = 'uploading'
        item.progress = 0
        item.error = undefined
        item.speed = speed.stats

        try {
            const formData = new FormData()
            formData.append(target.field, item.file, item.file.name)
            const response = await uploadWithProgress<UploadResponse>(target.url, formData, {
                speed,
                signal: controller.signal,
                onProgress: (event) => {
                    if (event.lengthComputable) item.progress = event.loaded / event.total * 100
                },
            })
            if (!response.success) throw new Error(response.message)
            item.contentId = response.data.mongoDbDocument.contentId.toString()
            item.progress = 100
            item.status = 'done'
            completed.value[item.kind]++
        } catch (error) {
            if (error instanceof UploadRequestError && error.kind === 'aborted') {
                item.status = 'cancelled'
            } else {
                console.error('Upload failed', item.name, error)
                item.status = 'error'
                item.error = error instanceof UploadRequestError
                    ? error.kind === 'network'
                        ? { code: 'network' }
                        : { code: 'http', status: error.status, message: error.payload?.statusMessage || error.payload?.message }
                    : { code: 'http', message: error instanceof Error ? error.message : undefined }
            }
        } finally {
            item.speed = undefined
            scope.stop()
            running.delete(id)
            pump()
        }
    }

    /** Queues `files` for the `kind` gallery, rejecting unsupported and oversized ones straight away. */
    const add = (kind: UploadKind, files: Iterable<File>) => {
        const target = UPLOAD_TARGETS[kind]
        for (const file of files) {
            const item: UploadItem = {
                id: crypto.randomUUID(),
                kind,
                file: markRaw(file),
                name: file.name,
                size: file.size,
                status: 'queued',
                progress: 0,
            }
            const tooLarge = uploadLimits[kind].tooLargeMessage(file)
            if (target.isSupported && !target.isSupported(file)) {
                item.status = 'error'
                item.error = { code: 'unsupported' }
            } else if (tooLarge) {
                item.status = 'error'
                item.error = { code: 'tooLarge', message: tooLarge }
            }
            items.value.push(item)
        }
        pump()
    }

    /**
     * Queues the files of a folder for the `kind` gallery. Unlike picked files, a folder holds whatever happened to be
     * in it, so files the gallery can't take are skipped instead of failing. Hidden files and folders (.DS_Store, .git
     * and the like) are left out silently, as nobody meant to upload those.
     */
    const addFolder = (kind: UploadKind, files: Iterable<File>) => {
        const isSupported = UPLOAD_TARGETS[kind].isSupported
        const accepted: File[] = []
        for (const file of files) {
            // A picked folder gives each file's path, starting with the folder itself, which may be hidden on purpose.
            // A dropped folder has its hidden subfolders left out while it is read.
            const path = file.webkitRelativePath ? file.webkitRelativePath.split('/').slice(1) : [file.name]
            if (path.some(part => part.startsWith('.'))) continue
            if (!isSupported || isSupported(file)) accepted.push(file)
            else skipped.value++
        }
        add(kind, accepted)
    }

    const cancel = (id: string) => {
        const item = find(id)
        if (!item) return
        if (item.status === 'uploading') running.get(id)?.controller.abort()
        else if (item.status === 'queued') item.status = 'cancelled'
    }

    const cancelAll = () => pending.value.forEach(item => cancel(item.id))

    const retry = (id: string) => {
        const item = find(id)
        if (!item || (item.status !== 'error' && item.status !== 'cancelled')) return
        // A file rejected before upload would only be rejected again.
        if (item.error?.code === 'unsupported' || item.error?.code === 'tooLarge') return
        item.status = 'queued'
        item.error = undefined
        item.progress = 0
        pump()
    }

    const remove = (id: string) => {
        cancel(id)
        items.value = items.value.filter(item => item.id !== id)
    }

    /** Drops everything that is no longer queued or uploading. */
    const clearFinished = () => {
        items.value = items.value.filter(item => item.status === 'queued' || item.status === 'uploading')
        skipped.value = 0
    }

    return { items, completed, skipped, pending, isActive, totals, add, addFolder, cancel, cancelAll, retry, remove, clearFinished }
})
